/**
 * 同意文の版 `/reception/admin/consent`(reception:admin。設計書 §10.3)。
 * 版は不変で、足すだけ(直す・消すの口は無い)。直したいときは新しい版を足す。
 * 発効日時を先にした版は、その時刻まで受付に出ない(今の版のまま)。
 */
import { useMutation } from "@tanstack/react-query";
import { Alert, Button, DatePicker, Form, Input, Modal, Space, Typography, message } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useState } from "react";

import { DataTable, PageHeader, StatusTag, type DataColumn } from "../../ui";
import {
  errorText,
  useConsentVersions,
  useInvalidateReception,
  useReceptionPerms,
  useSend,
  type ConsentVersion,
} from "./hooks";

const { Paragraph, Text } = Typography;

type ConsentForm = { version: string; body_ja: string; body_en?: string; effective_from?: Dayjs | null };

export function ConsentPage() {
  const { canAdmin, readOnly } = useReceptionPerms();
  const query = useConsentVersions();
  const rows = query.data?.items ?? [];
  const [adding, setAdding] = useState(false);
  const [viewing, setViewing] = useState<ConsentVersion | null>(null);

  const columns: DataColumn<ConsentVersion>[] = [
    { title: "版", ellipsis: false, dataIndex: "version", width: 160, render: (v: string) => <span className="ui-num">{v}</span> },
    {
      title: "状態",
      ellipsis: false,
      width: 110,
      render: (_: unknown, v) =>
        v.current ? (
          <StatusTag tone="success">現行</StatusTag>
        ) : v.future ? (
          <StatusTag tone="info">予定</StatusTag>
        ) : (
          <StatusTag tone="neutral">過去</StatusTag>
        ),
    },
    {
      title: "発効",
      ellipsis: false,
      dataIndex: "effective_from",
      width: 160,
      render: (v: string) => dayjs(v).format("YYYY-MM-DD HH:mm"),
    },
    {
      title: "本文(日本語)",
      ellipsis: true,
      onCell: (row) => ({ title: row.body_ja }),
      dataIndex: "body_ja",
      width: 360,
      render: (v: string) => <span className="ui-cell-sub">{v.length > 60 ? `${v.slice(0, 60)}…` : v}</span>,
    },
    { title: "英語", ellipsis: false, width: 90, render: (_: unknown, v) => (v.body_en ? "あり" : "—") },
  ];

  return (
    <div className="ui-list">
      <div className="ui-list-main">
        <PageHeader
          title="同意文"
          description="受付で来訪者に見せる個人情報の取扱いの文。版は直せない・消せない(誰がどの版に同意したかを残すため)。直すときは新しい版を足す。"
          primary={
            canAdmin && !readOnly ? (
              <Button type="primary" onClick={() => setAdding(true)}>
                版を足す
              </Button>
            ) : undefined
          }
        />
        {query.isError && <Alert type="error" showIcon message="同意文を取得できなかった" description={errorText(query.error)} />}
        {query.isSuccess && query.data.current_id === null && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="今有効な同意文が無い。受付で同意を取れないので、版を足す"
          />
        )}
        <DataTable<ConsentVersion>
          columns={columns}
          scrollX={columns.reduce((sum, c) => sum + c.width, 0)}
          rows={rows}
          rowKey={(r) => r.id}
          loading={query.isFetching}
          total={rows.length}
          pageSize={Math.max(rows.length, 1)}
          onPage={() => undefined}
          onRowClick={(row) => setViewing(row)}
          rowTone={(row) => (row.current || row.future ? undefined : "neutral")}
          error={query.isError ? "取得できなかったので表示できない" : undefined}
          empty={{ text: "同意文の版がない" }}
        />
      </div>
      <Modal
        open={viewing !== null}
        width={720}
        title={viewing ? `同意文 ${viewing.version}` : ""}
        onCancel={() => setViewing(null)}
        footer={<Button onClick={() => setViewing(null)}>閉じる</Button>}
      >
        {viewing && (
          <>
            <Text type="secondary">
              発効 {dayjs(viewing.effective_from).format("YYYY-MM-DD HH:mm")}(この版は直せない)
            </Text>
            <Paragraph style={{ whiteSpace: "pre-wrap", marginTop: 12 }}>{viewing.body_ja}</Paragraph>
            {viewing.body_en && <Paragraph style={{ whiteSpace: "pre-wrap" }}>{viewing.body_en}</Paragraph>}
          </>
        )}
      </Modal>
      <AddVersion open={adding} onClose={() => setAdding(false)} />
    </div>
  );
}

function AddVersion({ open, onClose }: { open: boolean; onClose: () => void }) {
  const send = useSend();
  const invalidate = useInvalidateReception();
  const [form] = Form.useForm<ConsentForm>();
  const save = useMutation({
    mutationFn: (v: ConsentForm) =>
      send("POST", "/consent-versions", {
        version: v.version.trim(),
        body_ja: v.body_ja,
        body_en: v.body_en ?? "",
        ...(v.effective_from ? { effective_from: v.effective_from.toISOString() } : {}),
      }),
    onSuccess: () => {
      void message.success("版を足した(監査ログに記録した)");
      invalidate();
      form.resetFields();
      onClose();
    },
    onError: (e) => void message.error(errorText(e)),
  });
  return (
    <Modal
      open={open}
      width={720}
      title="同意文の版を足す"
      onCancel={onClose}
      footer={
        <Space>
          <Button onClick={onClose}>やめる</Button>
          <Button type="primary" loading={save.isPending} onClick={() => form.submit()}>
            足す
          </Button>
        </Space>
      }
    >
      <Alert type="info" showIcon style={{ marginBottom: 12 }} message="足した版は直せない・消せない。送る前に読み返す" />
      <Form<ConsentForm> form={form} layout="vertical" disabled={save.isPending} onFinish={(v) => save.mutate(v)}>
        <Form.Item name="version" label="版の名前" rules={[{ required: true, whitespace: true, message: "版の名前を入れる" }]}>
          <Input maxLength={50} placeholder="2026-10" style={{ width: 200 }} />
        </Form.Item>
        <Form.Item
          name="effective_from"
          label="発効日時"
          extra="空なら今すぐ。過去には遡れない(もう同意を取った来訪の版が変わってしまう)"
        >
          <DatePicker showTime disabledDate={(d) => d.isBefore(dayjs().startOf("day"))} />
        </Form.Item>
        <Form.Item name="body_ja" label="本文(日本語)" rules={[{ required: true, whitespace: true, message: "本文を入れる" }]}>
          <Input.TextArea rows={8} maxLength={20000} />
        </Form.Item>
        <Form.Item name="body_en" label="本文(英語・任意)">
          <Input.TextArea rows={6} maxLength={20000} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
